/**
 * The Anagrafica from the invoices (1.73, decisions of 14/10/2026): what the
 * invoices read from Gestify, and their electronic invoices, say about the
 * subjects — subjects missing, roles missing, address or IBAN empty or
 * different, names that are aliases — as proposals the Tesoreria confirms
 * one by one. Nothing changes by itself; a proposal dismissed does not come
 * back until the invoices bring a new value.
 *
 * 1.77 (decisions of 15/10/2026): subjects are matched only by VAT number or
 * tax code, never by name; the role comes from the document (passive →
 * Fornitore, active → Cliente) and a subject can have both.
 */
import { z } from 'zod';
import { normaliseIban, normaliseTaxId, nameKey, sameTaxSubject, subjectByTaxIds, taxIdKey, } from './tax-ids.js';
import { idSchema, optionalText, requiredText } from './validation.js';
function block(xml, tag) {
    const match = new RegExp(`<(?:\\w+:)?${tag}>([\\s\\S]*?)</(?:\\w+:)?${tag}>`).exec(xml);
    return match ? match[1] : null;
}
function value(xml, tag) {
    if (!xml)
        return null;
    const found = block(xml, tag);
    if (found === null)
        return null;
    const text = found
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();
    return text || null;
}
function party(xml) {
    if (!xml)
        return null;
    const data = block(xml, 'DatiAnagrafici');
    const vat = block(data ?? '', 'IdFiscaleIVA');
    const registry = block(data ?? '', 'Anagrafica');
    const seat = block(xml, 'Sede');
    const company = value(registry, 'Denominazione');
    const person = [value(registry, 'Nome'), value(registry, 'Cognome')].filter(Boolean).join(' ');
    const country = value(vat, 'IdPaese');
    const code = value(vat, 'IdCodice');
    const street = value(seat, 'Indirizzo');
    const number = value(seat, 'NumeroCivico');
    return {
        name: company ?? (person || null),
        vatNumber: code ? normaliseTaxId(`${country ?? ''}${code}`) : null,
        country: value(seat, 'Nazione') ?? country,
        taxCode: value(data, 'CodiceFiscale'),
        address: street ? (number ? `${street} ${number}` : street) : null,
        postalCode: value(seat, 'CAP'),
        city: value(seat, 'Comune'),
        province: value(seat, 'Provincia'),
    };
}
/** The two parties of an electronic invoice and the IBAN of its payment. */
export function invoiceParties(xml) {
    const iban = value(block(xml, 'DettaglioPagamento'), 'IBAN');
    return {
        supplier: party(block(xml, 'CedentePrestatore')),
        customer: party(block(xml, 'CessionarioCommittente')),
        iban: iban ? normaliseIban(iban) : null,
    };
}
/**
 * The counterparty among the two parties: the one with the identifiers or the
 * name Gestify gave the invoice, else the customer of an issued invoice and the
 * supplier of a received one (the supplier of a self-billing invoice).
 */
export function counterpartyParty(invoice, parties) {
    const candidates = [parties.supplier, parties.customer].filter((item) => item !== null);
    const ids = [invoice.counterpartyVat, invoice.counterpartyTaxCode].map(taxIdKey).filter(Boolean);
    const byIds = candidates.find((item) => [item.vatNumber, item.taxCode].map(taxIdKey).some((key) => key && ids.includes(key)));
    if (byIds)
        return byIds;
    const byName = candidates.find((item) => item.name && nameKey(item.name) === nameKey(invoice.counterpartyName));
    if (byName)
        return byName;
    if (invoice.selfBilling)
        return parties.supplier;
    return invoice.direction === 'emessa' ? parties.customer : parties.supplier;
}
/** The role an invoice gives its counterparty: a client issues nothing to us. */
export function roleOfInvoice(invoice) {
    return invoice.direction === 'emessa' && !invoice.selfBilling ? 'C' : 'F';
}
export const REGISTRY_DATA_FIELDS = [
    'address',
    'postalCode',
    'city',
    'province',
    'country',
    'iban',
];
export const REGISTRY_DATA_LABELS = {
    address: 'Indirizzo',
    postalCode: 'CAP',
    city: 'Comune',
    province: 'Provincia',
    country: 'Nazione',
    iban: 'IBAN (ruolo Fornitore)',
};
/** The country of a VAT number with a foreign prefix («IE6388047V»), else Italy. */
function countryOfVat(vatNumber) {
    const prefix = /^([A-Z]{2})/.exec(vatNumber ? normaliseTaxId(vatNumber) : '')?.[1];
    return prefix ?? 'IT';
}
/** What two addresses have in common once case, spaces and punctuation go. */
function sameText(a, b) {
    const key = (value) => (value ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    return key(a) === key(b);
}
function latest(items) {
    return [...items].sort((a, b) => b.date.localeCompare(a.date) || b.number.localeCompare(a.number, 'it'))[0];
}
const proposalInvoice = (item) => ({
    id: item.id,
    number: item.number,
    date: item.date,
});
/** The key of a data proposal: the value is part of it, so a new value proposes again. */
export function dataKey(subjectId, field, value) {
    return `dato|${subjectId}|${field}|${value.toUpperCase().replace(/[^A-Z0-9]/g, '')}`;
}
/**
 * The proposals of the invoices: credit notes and pro forma speak of the same
 * subjects as the invoices, so all documents count; what the Anagrafica
 * already has, and what was dismissed, is not proposed.
 */
export function registryProposals(invoices, subjects, dismissed = new Set()) {
    const byId = new Map(subjects.map((subject) => [subject.id, subject]));
    const byName = new Map();
    for (const subject of subjects) {
        for (const name of [subject.name, ...subject.aliases]) {
            const key = nameKey(name);
            if (key && !byName.has(key))
                byName.set(key, subject);
        }
    }
    // The documents without a subject, by identifier, else by name (linked by hand).
    const groups = new Map();
    for (const invoice of invoices) {
        if (invoice.subjectId)
            continue;
        const vat = taxIdKey(invoice.counterpartyVat) ?? taxIdKey(invoice.party?.vatNumber);
        const code = taxIdKey(invoice.counterpartyTaxCode) ?? taxIdKey(invoice.party?.taxCode);
        // In a Gruppo IVA the tax code names the company (2.31): by it when it differs from the VAT.
        const key = (code && vat && code !== vat ? code : (vat ?? code)) ??
            `nome:${nameKey(invoice.counterpartyName)}`;
        groups.set(key, [...(groups.get(key) ?? []), invoice]);
    }
    const add = [];
    const link = [];
    const manual = [];
    for (const [group, items] of groups) {
        const key = `nuovo|${group}`;
        if (dismissed.has(key))
            continue;
        const last = latest(items);
        const read = items.filter((item) => item.party);
        const party = read.length ? latest(read).party : null;
        const paid = items.filter((item) => item.iban);
        const vatNumber = last.counterpartyVat ?? party?.vatNumber ?? null;
        const taxCode = last.counterpartyTaxCode ?? party?.taxCode ?? null;
        const subject = subjectByTaxIds(subjects, { vatNumber, taxCode });
        const named = byName.get(nameKey(last.counterpartyName));
        const roles = [...new Set(items.map(roleOfInvoice))].sort();
        const passive = items.filter((item) => roleOfInvoice(item) === 'F').length;
        const proposal = {
            key,
            name: last.counterpartyName,
            vatNumber,
            taxCode,
            country: party?.country ?? countryOfVat(vatNumber),
            address: party?.address ?? null,
            postalCode: party?.postalCode ?? null,
            city: party?.city ?? null,
            province: party?.province ?? null,
            iban: roles.includes('F') && paid.length ? latest(paid).iban : null,
            roles,
            active: items.length - passive,
            passive,
            typeCode: vatNumber ? 'FPR' : 'FPF',
            invoices: items.length,
            invoiceIds: items.map((item) => item.id),
            last: proposalInvoice(last),
            subject: subject ? { id: subject.id, name: subject.name, roles: subject.roles } : null,
            newRoles: subject ? roles.filter((role) => !subject.roles.includes(role)) : [],
            sameName: named && named.id !== subject?.id
                ? { id: named.id, name: named.name, vatNumber: named.vatNumber }
                : null,
            opening: items.every((item) => item.opening),
            xmlRead: items.some((item) => item.party),
        };
        if (subject)
            link.push(proposal);
        else if (group.startsWith('nome:'))
            manual.push(proposal);
        else
            add.push(proposal);
    }
    const roles = [];
    const data = [];
    const aliases = [];
    const linked = new Map();
    for (const invoice of invoices) {
        if (!invoice.subjectId || !byId.has(invoice.subjectId))
            continue;
        linked.set(invoice.subjectId, [...(linked.get(invoice.subjectId) ?? []), invoice]);
    }
    for (const [subjectId, items] of linked) {
        const subject = byId.get(subjectId);
        // Roles: what the invoices make of it and it does not have.
        for (const roleCode of ['C', 'F']) {
            if (subject.roles.includes(roleCode))
                continue;
            const ofRole = items.filter((item) => roleOfInvoice(item) === roleCode);
            if (!ofRole.length)
                continue;
            const key = `ruolo|${subjectId}|${roleCode}`;
            if (dismissed.has(key))
                continue;
            roles.push({
                key,
                subjectId,
                subjectName: subject.name,
                roleCode,
                roles: subject.roles,
                invoices: ofRole.length,
                last: proposalInvoice(latest(ofRole)),
            });
        }
        // Data: the latest electronic invoice read, field by field.
        const read = items.filter((item) => item.party);
        if (read.length) {
            const last = latest(read);
            const party = last.party;
            for (const field of ['address', 'postalCode', 'city', 'province', 'country']) {
                const proposed = party[field];
                if (!proposed || sameText(subject[field], proposed))
                    continue;
                const key = dataKey(subjectId, field, proposed);
                if (dismissed.has(key))
                    continue;
                data.push({
                    key,
                    subjectId,
                    subjectName: subject.name,
                    field,
                    current: subject[field],
                    proposed,
                    last: proposalInvoice(last),
                });
            }
        }
        const paid = items.filter((item) => item.iban && roleOfInvoice(item) === 'F');
        if (paid.length && subject.roles.includes('F')) {
            const last = latest(paid);
            const proposed = last.iban;
            if (!sameText(subject.supplierIban, proposed)) {
                const key = dataKey(subjectId, 'iban', proposed);
                if (!dismissed.has(key)) {
                    data.push({
                        key,
                        subjectId,
                        subjectName: subject.name,
                        field: 'iban',
                        current: subject.supplierIban,
                        proposed,
                        last: proposalInvoice(last),
                    });
                }
            }
        }
        // Aliases: the names of its invoices that are neither its name nor an alias.
        const known = new Set([subject.name, ...subject.aliases].map(nameKey));
        const names = new Map();
        for (const item of items) {
            const key = nameKey(item.counterpartyName);
            if (!key || known.has(key))
                continue;
            // Another company of its Gruppo IVA, by the tax code (2.31): not an alias of this one.
            const ids = { vatNumber: item.counterpartyVat, taxCode: item.counterpartyTaxCode };
            if (taxIdKey(ids.taxCode) && subject.taxCode && !sameTaxSubject(subject, ids))
                continue;
            names.set(key, [...(names.get(key) ?? []), item]);
        }
        for (const [name, named] of names) {
            const key = `alias|${subjectId}|${name}`;
            if (dismissed.has(key))
                continue;
            // A name already an alias of another subject cannot be one of this.
            const other = byName.get(name);
            if (other && other.id !== subjectId)
                continue;
            const last = latest(named);
            aliases.push({
                key,
                subjectId,
                subjectName: subject.name,
                alias: last.counterpartyName,
                invoices: named.length,
                last: proposalInvoice(last),
            });
        }
    }
    const byNameOrder = (items) => items.sort((a, b) => a.subjectName.localeCompare(b.subjectName, 'it'));
    const byLatest = (items) => items.sort((a, b) => b.last.date.localeCompare(a.last.date) || a.name.localeCompare(b.name, 'it'));
    return {
        add: byLatest(add),
        link: byLatest(link),
        manual: byLatest(manual),
        roles: byNameOrder(roles),
        data: byNameOrder(data),
        aliases: byNameOrder(aliases),
    };
}
/** The number of proposals, for the menu and the end of a reading of Gestify. */
export function proposalCount(proposals) {
    return (proposals.add.length +
        proposals.link.length +
        proposals.manual.length +
        proposals.roles.length +
        proposals.data.length +
        proposals.aliases.length);
}
/** «Collega»: the documents go to the subject of their identifier, or to one chosen when they have none. */
export const proposalLinkSchema = z.object({
    key: requiredText(300),
    subjectId: idSchema,
});
/** «Crea soggetto»: the subject as the page shows it, changeable; the roles come from the documents. */
export const proposalCreateSchema = z.object({
    key: requiredText(300),
    typeCode: z.string().trim().min(1).max(30),
    name: requiredText(200),
    country: z.string().regex(/^[A-Z]{2}$/, { message: 'Paese non valido' }),
    vatNumber: optionalText(30),
    taxCode: optionalText(30),
    address: optionalText(200),
    postalCode: optionalText(12),
    city: optionalText(100),
    province: optionalText(40),
    iban: optionalText(40),
});
/** A proposal confirmed or dismissed by its key. */
export const proposalKeySchema = z.object({ key: requiredText(300) });
//# sourceMappingURL=registry-proposals.js.map